import * as React from 'react';
import { isNil, isEmpty, isEqual } from 'lodash';

import { ClientDataApi } from 'configuration-schemas/confdefnView';
import { getValidValues } from '../StyleEditSection/StyleEditSection.client';
import { getUrl } from '../StyleEdit.utils';
import InputSuggest, { Suggestion } from 'src/common-ui/components/Inputs/InputSuggest/InputSuggest';
import styles from './DividedColumnDetails.styles';
import { classes } from 'typestyle';
import ServiceContainer from 'src/ServiceContainer';
import { toast } from 'react-toastify';

export interface DependentPropDropdownProps {
  key: string;
  value: string;
  styleId: string;
  dataApi: ClientDataApi;
  dataQa: string;
  mandatory?: boolean;
  allowEmptyOption: boolean;
  concatOptionValues: boolean;
  ignoreCache?: boolean;
  disabled?: boolean;
  editable?: boolean;
  handleDropdownChange: (selection: any) => void;
  onClose?: () => void;
  /** When true, the dropdown will open immediately on mount */
  initiallyOpen?: boolean;
}

export interface DependentPropDropdownState {
  selectedIndex: number;
  options: Suggestion[];
  open: boolean;
}

export default class DependentPropDropdown extends React.Component<
  DependentPropDropdownProps,
  DependentPropDropdownState
> {
  constructor(props: DependentPropDropdownProps) {
    super(props);

    this.state = {
      options: [],
      selectedIndex: -1, // guarantees first update after initial selection
      open: this.props.initiallyOpen ?? false,
    };
  }

  componentDidMount() {
    this.getData();
  }

  componentDidUpdate(prevProps: DependentPropDropdownProps) {
    if (prevProps.styleId !== this.props.styleId) {
      this.getData(true);
    }
    if (!isEqual(prevProps.dataApi, this.props.dataApi)) {
      this.getData(true, this.props.mandatory);
    }
  }

  shouldComponentUpdate(nextProps: DependentPropDropdownProps, nextState: DependentPropDropdownState) {
    if (!isEqual(nextProps.dataApi, this.props.dataApi)) {
      return true; // dependent prop cleared, re-get options data
    }

    if (nextProps.styleId !== this.props.styleId) {
      return true; // when styleId is updated, will get new dropdown options
    }

    return (
      nextState.selectedIndex !== this.state.selectedIndex ||
      !isEqual(nextState.options, this.state.options) ||
      nextState.open !== this.state.open
    );
  }

  getData(refresh = false, mandatory = false) {
    const { dataApi, allowEmptyOption, concatOptionValues } = this.props;
    const url = getUrl(dataApi);

    getValidValues(url, allowEmptyOption, concatOptionValues)
      .then((validValues: Suggestion[]) => {
        // Either select the selected index or a blank value
        const prevSelectedIndex = !isEmpty(this.props.value) ? this.getSelectedIndex(validValues) : -1;
        let selectedIndex =
          prevSelectedIndex < 0 ? validValues.findIndex((opt) => opt.value === '') : prevSelectedIndex;

        // If mandatory, set to the first known value
        const foundIndex = validValues.findIndex((opt) => opt.value !== '' && !isEmpty(opt.value));
        if (mandatory && foundIndex > -1 && prevSelectedIndex < 0) {
          selectedIndex = foundIndex;
        }

        this.setState(
          {
            options: validValues,
            selectedIndex: selectedIndex < 0 ? 0 : selectedIndex, // default to first item if no match
          },
          () => {
            if (refresh) {
              // on options refresh notify parent that the first option is new data selection. Pick non-empty
              const { selectedIndex } = this.state;
              const index = selectedIndex < 0 ? 0 : selectedIndex;
              this.props.handleDropdownChange(validValues[index]);
            }
          },
        );
      })
      .catch((err) => {
        toast.error(`An error occurred fetching the options for a dropdown`);
        ServiceContainer.loggingService.error(`An error occured: ${err.message}`, err.stack);
      });
  }

  getSelectedIndex(options: any[]) {
    const { value } = this.props;
    return options.findIndex((option) => option.value === value);
  }

  handleSelection = (selection: Suggestion) => {
    const { options } = this.state;
    const selectedIndex = options.findIndex((option) => option.value === selection.value);
    const selectedValue = options[selectedIndex];

    this.setState({ selectedIndex });
    this.props.handleDropdownChange(selectedValue);
  };

  render() {
    const { dataQa, disabled, editable, key } = this.props;
    const { selectedIndex, options, open } = this.state;
    const dropdownOptions = isNil(options) || isEmpty(options) ? [] : options;
    const selected = !isEmpty(dropdownOptions) ? options[selectedIndex] : undefined;

    if (!editable) {
      // display value from data, falling back to first option
      const selectedValue = !isNil(selected) ? selected.label : !isEmpty(dropdownOptions) && dropdownOptions[0].label;
      return (
        <div className={classes(styles.dataItem, styles.dropdownValue)} key={key}>
          <div className={styles.dropdownValue}>{selectedValue}</div>
        </div>
      );
    }
    return (
      <div className={styles.dataItem}>
        <InputSuggest
          validValues={dropdownOptions}
          multiSelect={false}
          autofocus={true}
          selected={selected}
          onSelect={this.handleSelection}
          dataQa={dataQa}
          disabled={disabled}
          open={open}
          onOpen={() => {
            // When InputSuggest opens, set open to true (if not already)
            this.setState({ open: true });
          }}
          onClose={() => {
            this.setState({ open: false });
            if (this.props.onClose) {
              this.props.onClose();
            }
          }}
          allowPortal={true}
        />
      </div>
    );
  }
}
